import { useCallback, useEffect, useMemo, useState } from 'react';
import { NavLink, Route, Routes } from 'react-router';
import {
  monthsBetween,
  type AccessDto,
  type AccessPageDto,
  type AuditEventDto,
  type CollectStatusDto,
  type DevicesDto,
  type ExportDetail,
  type RetentionDto,
} from '@mnemo/shared';
import type { Locale, TranslationKey } from '../i18n.js';
import { api, ApiError } from '../lib/api.js';
import { EmptyState, ErrorState, Loading } from '../components/ui.js';

type T = (key: TranslationKey, vars?: Record<string, string | number>) => string;

/**
 * L'administration — réservée aux administrations, et le serveur y veille : chaque appel
 * sous `/api/admin` répond 404 à qui n'a pas le rôle. L'onglet n'est masqué ici que par
 * confort ; ce n'est pas lui qui protège.
 *
 * Cinq vues, chacune un segment de chemin (Charte §6) : on peut envoyer le lien exact.
 */
export function Admin({ t, locale }: { t: T; locale: Locale }) {
  const f = useFormats(locale);
  const vues: { to: string; key: TranslationKey }[] = [
    { to: '/admin', key: 'admin.nav.access' },
    { to: '/admin/journal', key: 'admin.nav.journal' },
    { to: '/admin/collecte', key: 'admin.nav.collect' },
    { to: '/admin/conservation', key: 'admin.nav.retention' },
    { to: '/admin/imprimantes', key: 'admin.nav.devices' },
  ];

  return (
    <main className="mx-auto max-w-7xl space-y-4 px-4 py-4">
      <nav aria-label={t('admin.nav.label')} className="flex flex-wrap gap-1.5">
        {vues.map((v) => (
          <NavLink
            key={v.to}
            to={v.to}
            end
            className={({ isActive }) =>
              `inline-flex min-h-[40px] items-center rounded-full border px-4 text-sm font-medium transition-colors ${
                isActive
                  ? 'border-accent bg-accent/15 text-fg'
                  : 'border-border text-muted hover:bg-surface hover:text-fg'
              }`
            }
          >
            {t(v.key)}
          </NavLink>
        ))}
      </nav>

      <Routes>
        <Route index element={<AccessView t={t} f={f} />} />
        <Route path="journal" element={<JournalView t={t} f={f} />} />
        <Route path="collecte" element={<CollectView t={t} f={f} />} />
        <Route path="conservation" element={<RetentionView t={t} f={f} />} />
        <Route path="imprimantes" element={<DevicesView t={t} f={f} />} />
      </Routes>
    </main>
  );
}

// ─── Formats ────────────────────────────────────────────────────────────────

type Formats = ReturnType<typeof useFormats>;

function useFormats(locale: Locale) {
  return useMemo(() => {
    const tag = locale === 'de' ? 'de-CH' : 'fr-CH';
    const zone = 'Europe/Zurich';
    const number = new Intl.NumberFormat(tag);
    const dateTime = new Intl.DateTimeFormat(tag, {
      timeZone: zone,
      dateStyle: 'medium',
      timeStyle: 'short',
    });
    const time = new Intl.DateTimeFormat(tag, { timeZone: zone, timeStyle: 'short' });
    const day = new Intl.DateTimeFormat(tag, {
      timeZone: zone,
      weekday: 'long',
      day: 'numeric',
      month: 'long',
    });
    const month = new Intl.DateTimeFormat(tag, { month: 'long', year: 'numeric', timeZone: 'UTC' });
    const relative = new Intl.RelativeTimeFormat(tag, { numeric: 'auto' });

    return {
      number: (n: number) => number.format(n),
      dateTime: (iso: string) => dateTime.format(new Date(iso)),
      time: (iso: string) => time.format(new Date(iso)),
      day: (iso: string) => day.format(new Date(iso)),
      /** « septembre 2025 » à partir de « 2025-09 ». */
      month: (period: string) => month.format(new Date(`${period}-01T12:00:00Z`)),
      /** « il y a 2 heures », « hier » — le lisible d'abord, l'exact au survol. */
      ago: (iso: string) => {
        const seconds = (new Date(iso).getTime() - Date.now()) / 1000;
        const abs = Math.abs(seconds);
        if (abs < 60) return relative.format(Math.round(seconds), 'second');
        if (abs < 3600) return relative.format(Math.round(seconds / 60), 'minute');
        if (abs < 86_400) return relative.format(Math.round(seconds / 3600), 'hour');
        if (abs < 30 * 86_400) return relative.format(Math.round(seconds / 86_400), 'day');
        return relative.format(Math.round(seconds / (30 * 86_400)), 'month');
      },
      /** La clé de regroupement par jour, à l'heure suisse. */
      dayKey: (iso: string) =>
        new Intl.DateTimeFormat('en-CA', { timeZone: zone }).format(new Date(iso)),
    };
  }, [locale]);
}

// ─── Chargement commun ──────────────────────────────────────────────────────

function useLoad<V>(fetcher: () => Promise<V>) {
  const [data, setData] = useState<V | null>(null);
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [code, setCode] = useState('INTERNAL');

  const load = useCallback(async () => {
    setStatus('loading');
    try {
      setData(await fetcher());
      setStatus('ready');
    } catch (err) {
      // Pas de repli silencieux : un échec se montre, avec de quoi réessayer.
      setCode(err instanceof ApiError ? err.code : 'INTERNAL');
      setStatus('error');
    }
  }, [fetcher]);

  useEffect(() => {
    void load();
  }, [load]);

  return { data, status, code, load };
}

// ─── Accès ──────────────────────────────────────────────────────────────────

type AccessFilter = 'all' | 'exported' | 'noScope' | 'admins';

function AccessView({ t, f }: { t: T; f: Formats }) {
  const fetcher = useCallback(() => api.admin.access(), []);
  const { data, status, code, load } = useLoad<AccessPageDto>(fetcher);
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState<AccessFilter>('all');

  const items = data?.items ?? [];
  const counts = useMemo(
    () => ({
      people: items.length,
      open: items.filter((i) => i.sessionOpen).length,
      exports: items.reduce((s, i) => s + i.exports, 0),
      noScope: items.filter((i) => !i.isAdmin && i.establishments.length === 0).length,
    }),
    [items],
  );

  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return items.filter((i) => {
      if (filter === 'exported' && i.exports === 0) return false;
      if (filter === 'noScope' && (i.isAdmin || i.establishments.length > 0)) return false;
      if (filter === 'admins' && !i.isAdmin) return false;
      if (!needle) return true;
      return [i.name, i.email, i.department ?? '', ...i.establishments]
        .join(' ')
        .toLowerCase()
        .includes(needle);
    });
  }, [items, q, filter]);

  if (status === 'error') return <ErrorState t={t} code={code} onRetry={() => void load()} />;
  if (!data) return <Loading t={t} />;

  const pills: { value: AccessFilter; key: TranslationKey }[] = [
    { value: 'all', key: 'admin.access.filter.all' },
    { value: 'exported', key: 'admin.access.filter.exported' },
    { value: 'noScope', key: 'admin.access.filter.noScope' },
    { value: 'admins', key: 'admin.access.filter.admins' },
  ];

  return (
    <div className="space-y-4">
      <dl className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Tile label={t('admin.access.kpi.people')} value={f.number(counts.people)} />
        <Tile
          label={t('admin.access.kpi.open')}
          value={f.number(counts.open)}
          hint={t('admin.access.kpi.openHint')}
        />
        <Tile label={t('admin.access.kpi.exports')} value={f.number(counts.exports)} />
        <Tile
          label={t('admin.access.kpi.noScope')}
          value={f.number(counts.noScope)}
          hint={t('admin.access.kpi.noScopeHint')}
          tone={counts.noScope > 0 ? 'danger' : undefined}
        />
      </dl>

      <section className="card space-y-3 p-3">
        <div className="flex flex-wrap items-center gap-2">
          <input
            type="search"
            className="field min-w-[16rem] flex-1"
            placeholder={t('admin.access.search')}
            aria-label={t('admin.access.search')}
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          <div className="flex flex-wrap gap-1.5" role="group">
            {pills.map((p) => (
              <button
                key={p.value}
                type="button"
                aria-pressed={filter === p.value}
                onClick={() => setFilter(p.value)}
                className={`min-h-[36px] rounded-full border px-3 text-sm transition-colors ${
                  filter === p.value
                    ? 'border-accent bg-accent/15 text-fg'
                    : 'border-border text-muted hover:text-fg'
                }`}
              >
                {t(p.key)}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="card overflow-hidden">
        {shown.length === 0 ? (
          <EmptyState message={t('admin.access.empty')} />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-bg text-left text-xs uppercase tracking-wide text-muted">
                <tr>
                  <th scope="col" className="px-3 py-2">
                    {t('admin.access.col.person')}
                  </th>
                  <th scope="col" className="px-3 py-2">
                    {t('admin.access.col.scope')}
                  </th>
                  <th scope="col" className="px-3 py-2">
                    {t('admin.access.col.lastLogin')}
                  </th>
                  <th scope="col" className="hidden px-3 py-2 text-right md:table-cell">
                    {t('admin.access.col.logins')}
                  </th>
                  <th scope="col" className="px-3 py-2">
                    {t('admin.access.col.exports')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {shown.map((i) => (
                  <AccessRow key={i.id} t={t} f={f} item={i} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <p className="text-xs text-muted">
        {data.journalSince
          ? t('admin.access.since', { date: f.dateTime(data.journalSince) })
          : t('admin.access.sinceNone')}
      </p>
    </div>
  );
}

function AccessRow({ t, f, item }: { t: T; f: Formats; item: AccessDto }) {
  const noScope = !item.isAdmin && item.establishments.length === 0;
  return (
    <tr className="border-t border-border align-top">
      <td className="px-3 py-2">
        <div className="flex items-center gap-2">
          {/* Le point vert dit « session ouverte », pas « en train de cliquer » : une session
              dure quelques heures, et une fermeture d'onglet ne la ferme pas. */}
          <span
            aria-hidden="true"
            className={`h-2 w-2 shrink-0 rounded-full ${item.sessionOpen ? 'bg-accent' : 'bg-border'}`}
          />
          <span className="font-medium">{item.name}</span>
          {item.isAdmin && <span className="chip bg-blue/15 text-fg">{t('admin.badge')}</span>}
        </div>
        <a href={`mailto:${item.email}`} className="ml-4 text-xs text-muted hover:text-fg">
          {item.email}
        </a>
        {item.sessionOpen && <span className="sr-only">{t('admin.access.sessionOpen')}</span>}
      </td>
      <td className="px-3 py-2">
        {item.isAdmin ? (
          <span className="text-muted">{t('admin.access.scopeAll')}</span>
        ) : noScope ? (
          <div>
            <span className="chip bg-danger/15 text-fg">{t('admin.access.scopeNone')}</span>
            {/* Le premier cas de support : on donne tout de suite la piste. */}
            <p className="mt-1 text-xs text-muted">
              {item.department
                ? t('admin.access.department', { department: item.department })
                : t('admin.access.departmentNone')}
            </p>
          </div>
        ) : (
          <div className="flex flex-wrap gap-1">
            {item.establishments.map((e) => (
              <span key={e} className="chip bg-accent/15 text-fg">
                {e}
              </span>
            ))}
          </div>
        )}
      </td>
      <td className="whitespace-nowrap px-3 py-2">
        <span title={f.dateTime(item.lastLoginAt)}>{f.ago(item.lastLoginAt)}</span>
        <p className="text-xs text-muted" title={f.dateTime(item.firstSeenAt)}>
          {t('admin.access.firstSeen', { date: f.dateTime(item.firstSeenAt) })}
        </p>
      </td>
      <td className="hidden px-3 py-2 text-right tabular-nums md:table-cell">
        {f.number(item.logins)}
      </td>
      <td className="px-3 py-2">
        {item.exports === 0 ? (
          <span className="text-muted">—</span>
        ) : (
          <div className="space-y-1">
            <span className="chip bg-warn/15 text-fg">
              {t('admin.access.exportCount', { count: f.number(item.exports) })}
            </span>
            {item.lastExport && item.lastExportAt && (
              <ExportNote t={t} f={f} at={item.lastExportAt} detail={item.lastExport} />
            )}
          </div>
        )}
      </td>
    </tr>
  );
}

/** Ce qui est sorti, en une ligne : quand, combien, avec quels filtres. */
function ExportNote({ t, f, at, detail }: { t: T; f: Formats; at: string; detail: ExportDetail }) {
  return (
    <div className="text-xs text-muted">
      <span title={f.dateTime(at)}>
        {t('admin.export.summary', { when: f.ago(at), rows: f.number(detail.rows) })}
      </span>
      {detail.truncated && <span className="ml-1 text-warn">· {t('admin.export.truncated')}</span>}
      <FilterChips t={t} f={f} detail={detail} />
    </div>
  );
}

function FilterChips({ t, f, detail }: { t: T; f: Formats; detail: ExportDetail }) {
  const chips: string[] = [];
  const x = detail.filters;
  if (detail.report === 'decompte') chips.push(t('nav.decompte'));
  if (x.establishment) chips.push(x.establishment);
  if (x.userEstablishment) chips.push(t('table.visitor', { code: x.userEstablishment }));
  if (x.costCenter) chips.push(x.costCenter);
  if (x.period) chips.push(f.month(x.period));
  if (x.from || x.to) chips.push(`${x.from ?? '…'} → ${x.to ?? '…'}`);
  if (x.type) chips.push(t(`type.${x.type}` as TranslationKey));
  if (x.user) chips.push(x.user);
  if (x.device) chips.push(x.device);
  if (x.q) chips.push(`« ${x.q} »`);
  if (x.color) chips.push(t('filter.colorOnly'));
  if (chips.length === 0) chips.push(t('admin.export.noFilter'));
  return (
    <div className="mt-0.5 flex flex-wrap gap-1">
      {chips.map((c, i) => (
        <span key={i} className="rounded border border-border px-1.5 py-0.5 text-[11px] text-fg">
          {c}
        </span>
      ))}
    </div>
  );
}

// ─── Journal ────────────────────────────────────────────────────────────────

type JournalFilter = 'all' | 'login' | 'export';

function JournalView({ t, f }: { t: T; f: Formats }) {
  const [filter, setFilter] = useState<JournalFilter>('all');
  const fetcher = useCallback(
    () => api.admin.events(filter === 'all' ? undefined : filter),
    [filter],
  );
  const { data, status, code, load } = useLoad(fetcher);

  // Regroupé par jour, à l'heure suisse : c'est ainsi qu'on lit un journal.
  const days = useMemo(() => {
    const groups = new Map<string, AuditEventDto[]>();
    for (const e of data?.items ?? []) {
      const key = f.dayKey(e.at);
      groups.set(key, [...(groups.get(key) ?? []), e]);
    }
    return [...groups.entries()];
  }, [data, f]);

  const pills: { value: JournalFilter; key: TranslationKey }[] = [
    { value: 'all', key: 'admin.journal.filter.all' },
    { value: 'login', key: 'admin.journal.filter.login' },
    { value: 'export', key: 'admin.journal.filter.export' },
  ];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-1.5" role="group">
        {pills.map((p) => (
          <button
            key={p.value}
            type="button"
            aria-pressed={filter === p.value}
            onClick={() => setFilter(p.value)}
            className={`min-h-[36px] rounded-full border px-3 text-sm transition-colors ${
              filter === p.value
                ? 'border-accent bg-accent/15 text-fg'
                : 'border-border text-muted hover:text-fg'
            }`}
          >
            {t(p.key)}
          </button>
        ))}
        <span className="ml-auto text-xs text-muted">{t('admin.journal.limit')}</span>
      </div>

      {status === 'error' && <ErrorState t={t} code={code} onRetry={() => void load()} />}
      {status === 'loading' && !data && <Loading t={t} />}
      {data && days.length === 0 && (
        <section className="card">
          <EmptyState message={t('admin.journal.empty')} />
        </section>
      )}

      {days.map(([key, events]) => (
        <section key={key} className="card overflow-hidden">
          <h3 className="border-b border-border bg-bg px-3 py-2 text-xs font-semibold uppercase tracking-wide text-muted">
            {f.day(events[0]!.at)}
          </h3>
          <ol>
            {events.map((e) => (
              <li
                key={e.id}
                className="flex gap-3 border-t border-border px-3 py-2 first:border-t-0"
              >
                <span className="w-12 shrink-0 pt-0.5 text-xs tabular-nums text-muted">
                  {f.time(e.at)}
                </span>
                <span
                  aria-hidden="true"
                  className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${
                    e.kind === 'export' ? 'bg-warn' : 'bg-blue'
                  }`}
                />
                <div className="min-w-0 flex-1 text-sm">
                  <span className="font-medium">{e.userName ?? e.userId}</span>{' '}
                  <span className="text-muted">
                    {e.kind === 'export'
                      ? t('admin.journal.exported', { rows: f.number(e.detail?.rows ?? 0) })
                      : t('admin.journal.loggedIn')}
                  </span>
                  {e.kind === 'export' && e.detail && (
                    <>
                      {e.detail.truncated && (
                        <span className="ml-1 text-xs text-warn">
                          · {t('admin.export.truncated')}
                        </span>
                      )}
                      <FilterChips t={t} f={f} detail={e.detail} />
                    </>
                  )}
                </div>
              </li>
            ))}
          </ol>
        </section>
      ))}
    </div>
  );
}

// ─── Collecte ───────────────────────────────────────────────────────────────

function CollectView({ t, f }: { t: T; f: Formats }) {
  const fetcher = useCallback(() => api.admin.collect(), []);
  const { data, status, code, load } = useLoad<CollectStatusDto>(fetcher);

  if (status === 'error') return <ErrorState t={t} code={code} onRetry={() => void load()} />;
  if (!data) return <Loading t={t} />;

  const state = data.collect?.state ?? 'pending';
  const tone = {
    ok: 'border-accent/50 bg-accent/10',
    pending: 'border-blue/50 bg-blue/10',
    stale: 'border-warn/60 bg-warn/10',
    failing: 'border-danger/60 bg-danger/10',
  }[state];

  const coverageMonths =
    data.coverage.oldestMonth && data.coverage.newestMonth
      ? monthsBetween(data.coverage.oldestMonth, data.coverage.newestMonth)
      : [];

  return (
    <div className="space-y-4">
      <section className={`rounded-xl border p-4 ${tone}`}>
        <p className="text-base font-semibold">
          {t(`admin.collect.state.${state}` as TranslationKey)}
        </p>
        <p className="mt-1 text-sm">
          {data.collect?.ageHours != null
            ? t('admin.collect.age', {
                hours: f.number(data.collect.ageHours),
                threshold: f.number(data.collect.thresholdHours),
              })
            : t('admin.collect.never')}
        </p>
        <p className="mt-1 text-sm text-muted">
          {t('admin.collect.schedule', { hour: data.scheduleHour, days: data.lateDays })}
        </p>
      </section>

      <section className="card space-y-2 p-4">
        <div className="flex items-baseline justify-between gap-2">
          <h3 className="font-semibold">{t('admin.collect.coverage')}</h3>
          <span className="text-sm text-muted">
            {t('admin.collect.coverageCount', {
              months: data.coverage.months,
              expected: data.coverage.expected,
            })}
          </span>
        </div>
        {/* Une case par mois : un trou se voit avant de se lire. */}
        <ol className="flex flex-wrap gap-1">
          {coverageMonths.map((m) => {
            const missing = data.coverage.missing.includes(m);
            return (
              <li
                key={m}
                title={`${f.month(m)}${missing ? ` — ${t('admin.collect.missing')}` : ''}`}
                className={`rounded px-2 py-1 text-xs tabular-nums ${
                  missing ? 'bg-danger/20 text-fg ring-1 ring-danger' : 'bg-accent/15 text-fg'
                }`}
              >
                {m}
              </li>
            );
          })}
        </ol>
        {data.coverage.missing.length > 0 && (
          <p className="text-sm text-danger">
            {t('admin.collect.missingHint', { months: data.coverage.missing.join(', ') })}
          </p>
        )}
      </section>

      <section className="card overflow-hidden">
        <h3 className="border-b border-border px-3 py-2 font-semibold">
          {t('admin.collect.runs')}
        </h3>
        {data.runs.length === 0 ? (
          <EmptyState message={t('admin.collect.runsEmpty')} />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-bg text-left text-xs uppercase tracking-wide text-muted">
                <tr>
                  <th className="px-3 py-2">{t('admin.collect.col.night')}</th>
                  <th className="px-3 py-2">{t('admin.collect.col.month')}</th>
                  <th className="px-3 py-2">{t('admin.collect.col.status')}</th>
                  <th className="px-3 py-2 text-right">{t('admin.collect.col.attempts')}</th>
                  <th className="px-3 py-2 text-right">{t('admin.collect.col.rows')}</th>
                  <th className="px-3 py-2">{t('admin.collect.col.error')}</th>
                </tr>
              </thead>
              <tbody>
                {data.runs.map((r) => (
                  <tr key={`${r.slot}-${r.period}`} className="border-t border-border">
                    <td className="whitespace-nowrap px-3 py-2 tabular-nums">{r.slot}</td>
                    <td className="px-3 py-2 tabular-nums">{r.period}</td>
                    <td className="px-3 py-2">
                      <StatusChip t={t} status={r.status} />
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">{r.attempts}</td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {r.rowsStored == null ? '—' : f.number(r.rowsStored)}
                    </td>
                    <td
                      className="max-w-[24rem] truncate px-3 py-2 text-xs text-danger"
                      title={r.error ?? ''}
                    >
                      {r.error ?? ''}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="card overflow-hidden">
        <h3 className="border-b border-border px-3 py-2 font-semibold">
          {t('admin.collect.ingests')}
        </h3>
        {data.ingests.length === 0 ? (
          <EmptyState message={t('admin.collect.ingestsEmpty')} />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-bg text-left text-xs uppercase tracking-wide text-muted">
                <tr>
                  <th className="px-3 py-2">{t('admin.collect.col.when')}</th>
                  <th className="px-3 py-2">{t('admin.collect.col.month')}</th>
                  <th className="px-3 py-2">{t('admin.collect.col.status')}</th>
                  <th className="px-3 py-2 text-right">{t('admin.collect.col.read')}</th>
                  <th className="px-3 py-2 text-right">{t('admin.collect.col.stored')}</th>
                  <th className="px-3 py-2 text-right">{t('admin.collect.col.rejected')}</th>
                  <th className="px-3 py-2">{t('admin.collect.col.header')}</th>
                  <th className="px-3 py-2">{t('admin.collect.col.error')}</th>
                </tr>
              </thead>
              <tbody>
                {data.ingests.map((i, n) => {
                  const drift = i.headerMissing.length + i.headerUnknown.length > 0;
                  return (
                    <tr key={n} className="border-t border-border">
                      <td className="whitespace-nowrap px-3 py-2" title={f.dateTime(i.startedAt)}>
                        {f.ago(i.startedAt)}
                      </td>
                      <td className="px-3 py-2 tabular-nums">{i.period}</td>
                      <td className="px-3 py-2">
                        <StatusChip t={t} status={shownStatus(i)} />
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">{f.number(i.rowsRead)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">
                        {f.number(i.rowsStored)}
                      </td>
                      <td
                        className={`px-3 py-2 text-right tabular-nums ${i.rowsRejected > 0 ? 'text-warn' : ''}`}
                      >
                        {f.number(i.rowsRejected)}
                      </td>
                      <td className="px-3 py-2 text-xs">
                        {/* Les 77 colonnes relevées dans les gabarits Power BI : un écart ici
                            est le premier signe d'une évolution du fournisseur. */}
                        {drift ? (
                          <span
                            className="text-warn"
                            title={[...i.headerMissing, ...i.headerUnknown].join(', ')}
                          >
                            {t('admin.collect.headerDrift', {
                              missing: i.headerMissing.length,
                              unknown: i.headerUnknown.length,
                            })}
                          </span>
                        ) : (
                          <span className="text-muted">{t('admin.collect.headerOk')}</span>
                        )}
                      </td>
                      <td
                        className="max-w-[24rem] truncate px-3 py-2 text-xs text-danger"
                        title={i.error ?? ''}
                      >
                        {i.error ?? ''}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

/**
 * Une ingestion « en cours » depuis plus d'une heure ne l'est plus : elle est morte sans
 * pouvoir le dire. La plus grosse prend ~16 s.
 *
 * 🪤 C'est le défaut que l'agent de déploiement a signalé le 23.09.2026 : une erreur en
 * cours d'ingestion laissait la ligne en `running`, sans motif — et quelqu'un qui la lit
 * trois jours plus tard croit à un traitement qui n'a jamais fini. Le code a été corrigé
 * pour écrire `failed`, mais les lignes laissées par les versions précédentes restent en
 * base. L'écran ne les fait pas passer pour vivantes.
 */
const ABANDONED_AFTER_MS = 3_600_000;

function shownStatus(i: { status: string; startedAt: string; finishedAt: string | null }) {
  const old = Date.now() - new Date(i.startedAt).getTime() > ABANDONED_AFTER_MS;
  return i.status === 'running' && !i.finishedAt && old ? 'abandoned' : i.status;
}

function StatusChip({ t, status }: { t: T; status: string }) {
  const tone =
    status === 'done' || status === 'ok'
      ? 'bg-accent/15'
      : status === 'failed' || status === 'abandoned'
        ? 'bg-danger/15'
        : 'bg-blue/15';
  const known = ['done', 'ok', 'failed', 'running', 'pending', 'abandoned'].includes(status);
  return (
    <span className={`chip ${tone} text-fg`}>
      {known ? t(`admin.status.${status}` as TranslationKey) : status}
    </span>
  );
}

// ─── Conservation ───────────────────────────────────────────────────────────

function RetentionView({ t, f }: { t: T; f: Formats }) {
  const fetcher = useCallback(() => api.admin.retention(), []);
  const { data, status, code, load } = useLoad<RetentionDto>(fetcher);

  if (status === 'error') return <ErrorState t={t} code={code} onRetry={() => void load()} />;
  if (!data) return <Loading t={t} />;

  return (
    <div className="space-y-4">
      <section className="card space-y-3 p-4">
        <p className="text-lg font-semibold">
          {t('admin.retention.window', { months: data.retentionMonths })}
        </p>
        {data.oldestMonth && data.oldestLeavesIn ? (
          <p className="text-sm">
            {t('admin.retention.oldest', {
              oldest: f.month(data.oldestMonth),
              leaves: f.month(data.oldestLeavesIn),
            })}
          </p>
        ) : (
          <p className="text-sm text-muted">{t('admin.retention.empty')}</p>
        )}
        <p className="text-sm text-muted">
          {t('admin.retention.audit', { months: data.retentionMonths })}
        </p>
      </section>

      {/* ⚠️ Cet écran sert de preuve : il ne doit rien affirmer qui ne soit vrai. La purge ne
          tourne pas seule aujourd'hui — il le dit, au lieu de laisser croire le contraire. */}
      {!data.automatic && (
        <section className="rounded-xl border border-warn/60 bg-warn/10 p-4 text-sm">
          <p className="font-semibold">{t('admin.retention.manualTitle')}</p>
          <p className="mt-1">
            {data.oldestLeavesIn
              ? t('admin.retention.manualBody', { leaves: f.month(data.oldestLeavesIn) })
              : t('admin.retention.manualBodyNoData')}
          </p>
        </section>
      )}

      <section className="card overflow-hidden">
        <h3 className="border-b border-border px-3 py-2 font-semibold">
          {t('admin.retention.log')}
        </h3>
        {data.purges.length === 0 ? (
          <EmptyState message={t('admin.retention.logEmpty')} />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-bg text-left text-xs uppercase tracking-wide text-muted">
                <tr>
                  <th className="px-3 py-2">{t('admin.collect.col.month')}</th>
                  <th className="px-3 py-2 text-right">{t('admin.retention.col.rows')}</th>
                  <th className="px-3 py-2">{t('admin.retention.col.reason')}</th>
                  <th className="px-3 py-2">{t('admin.retention.col.when')}</th>
                </tr>
              </thead>
              <tbody>
                {data.purges.map((p, n) => (
                  <tr key={n} className="border-t border-border">
                    <td className="px-3 py-2">{f.month(p.period)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{f.number(p.rowsDropped)}</td>
                    <td className="px-3 py-2">{p.reason}</td>
                    <td className="px-3 py-2">{f.dateTime(p.executedAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

// ─── Imprimantes ────────────────────────────────────────────────────────────

/**
 * Le contrôle de la règle D14 : chaque imprimante, l'établissement que son nom lui donne.
 *
 * Depuis que les droits suivent l'imprimante, un appareil mal nommé rend ses travaux
 * invisibles à toutes les écoles, sans erreur nulle part. Il apparaît ici en tête, en
 * alerte. La colonne « d'ailleurs » montre les machines partagées entre écoles.
 */
function DevicesView({ t, f }: { t: T; f: Formats }) {
  const fetcher = useCallback(() => api.admin.devices(), []);
  const { data, status, code, load } = useLoad<DevicesDto>(fetcher);

  if (status === 'error') return <ErrorState t={t} code={code} onRetry={() => void load()} />;
  if (!data) return <Loading t={t} />;

  const orphans = data.items.filter((d) => d.establishment === null);
  const orphanJobs = orphans.reduce((sum, d) => sum + d.jobs, 0);
  const attributed = new Set(data.items.map((d) => d.establishment).filter(Boolean)).size;
  const aliases = Object.entries(data.aliases)
    .map(([device, directory]) => `${device} → ${directory}`)
    .join(', ');

  return (
    <div className="space-y-4">
      <dl className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        <Tile label={t('admin.devices.kpi.devices')} value={f.number(data.items.length)} />
        <Tile label={t('admin.devices.kpi.establishments')} value={f.number(attributed)} />
        <Tile
          label={t('admin.devices.kpi.orphans')}
          value={f.number(orphans.length)}
          hint={t('admin.devices.kpi.orphansHint', { jobs: f.number(orphanJobs) })}
          tone={orphans.length > 0 ? 'danger' : undefined}
        />
      </dl>

      <section className="card space-y-1.5 p-4 text-sm">
        <p>{t('admin.devices.rule')}</p>
        {aliases && <p className="text-muted">{t('admin.devices.aliases', { aliases })}</p>}
      </section>

      <section className="card overflow-hidden">
        {data.items.length === 0 ? (
          <EmptyState message={t('admin.devices.empty')} />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-bg text-left text-xs uppercase tracking-wide text-muted">
                <tr>
                  <th className="px-3 py-2">{t('admin.devices.col.device')}</th>
                  <th className="px-3 py-2">{t('table.establishment')}</th>
                  <th className="px-3 py-2 text-right">{t('admin.devices.col.jobs')}</th>
                  <th className="px-3 py-2 text-right">{t('admin.devices.col.elsewhere')}</th>
                  <th className="hidden px-3 py-2 sm:table-cell">{t('admin.devices.col.last')}</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((d) => (
                  <tr
                    key={`${d.name ?? ''}|${d.establishment ?? ''}`}
                    className="border-t border-border"
                  >
                    <td className="px-3 py-2 font-mono text-xs">{d.name ?? '—'}</td>
                    <td className="px-3 py-2">
                      {d.establishment ? (
                        <span className="chip bg-accent/15 text-fg">{d.establishment}</span>
                      ) : (
                        <span className="chip bg-danger/15 text-danger">
                          {t('admin.devices.orphan')}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">{f.number(d.jobs)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {d.fromElsewhere > 0 ? (
                        <>
                          {f.number(d.fromElsewhere)}
                          <span className="ml-1 text-xs text-muted">
                            {Math.round((d.fromElsewhere / d.jobs) * 100)} %
                          </span>
                        </>
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </td>
                    <td
                      className="hidden px-3 py-2 text-muted sm:table-cell"
                      title={f.dateTime(d.lastAt)}
                    >
                      {f.ago(d.lastAt)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

// ─── Petits morceaux ────────────────────────────────────────────────────────

function Tile({
  label,
  value,
  hint,
  tone,
}: {
  label: string;
  value: string;
  hint?: string;
  tone?: 'danger';
}) {
  return (
    <div className={`card p-3 ${tone === 'danger' ? 'ring-1 ring-danger' : ''}`}>
      <dt className="text-xs text-muted">{label}</dt>
      <dd
        className={`text-2xl font-semibold tabular-nums ${tone === 'danger' ? 'text-danger' : ''}`}
      >
        {value}
      </dd>
      {hint && <p className="text-xs text-muted">{hint}</p>}
    </div>
  );
}
